import {
  useCreateSuperAdminClient,
  useSuperAdminClients,
  useUpdateSuperAdminShopAccount,
} from "@babackapp/private-api-client/client/use-super-admin-clients";
import type { SuperAdminShopAccountDto } from "@babackapp/private-api-client/types";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { Textarea } from "@babackapp/ui/components/textarea";
import { useState } from "react";
import { Link } from "react-router-dom";
import { MAX_LENGTH } from "./form-values";

export function ConfigurationSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border bg-background">
      <header className="border-b px-5 py-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        {description && (
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        )}
      </header>
      {children}
    </section>
  );
}

/**
 * The client the shop belongs to (BBS-406): one client groups the shops of a
 * company. Attach to an existing client, or create one with this shop.
 */
export function ShopClientSection({
  shopId,
  shopName,
  client,
}: {
  shopId: string;
  shopName: string;
  client: SuperAdminShopAccountDto["client"];
}) {
  const { data: clients } = useSuperAdminClients();
  const updateMutation = useUpdateSuperAdminShopAccount();
  const createMutation = useCreateSuperAdminClient();
  const [selectedClientId, setSelectedClientId] = useState("");
  // Empty, not `shopName`: on a deep link the shop list may not be loaded
  // yet and `shopName` is then the shop id.
  const [newClientName, setNewClientName] = useState("");

  function attach(clientAccountId: string | null) {
    updateMutation.mutate({
      params: { path: { shopId } },
      body: { clientAccountId },
    });
  }

  const isPending = updateMutation.isPending || createMutation.isPending;
  const otherClients = (clients ?? []).filter(({ id }) => id !== client?.id);

  return (
    <ConfigurationSection
      title="Client"
      description="Un client regroupe les boutiques d'une même entreprise : statut, CSM, importance et notes se gèrent sur sa fiche."
    >
      <div className="space-y-4 px-5 py-4">
        <div className="flex items-center justify-between gap-4">
          {client ? (
            <p className="text-sm">
              Rattachée à{" "}
              <Link
                to={`/admin/clients/${client.id}`}
                className="font-medium text-violet-700 hover:underline"
              >
                {client.name}
              </Link>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Rattachée à aucun client.
            </p>
          )}
          {client && (
            <Button
              variant="outline"
              size="sm"
              disabled={isPending}
              onClick={() => attach(null)}
            >
              Détacher
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={selectedClientId}
            onChange={(event) => setSelectedClientId(event.target.value)}
            className="h-8 min-w-64 rounded-md border bg-background px-2 text-sm"
          >
            <option value="">
              {client ? "Changer de client…" : "Choisir un client…"}
            </option>
            {otherClients.map(({ id, name, shops }) => (
              <option key={id} value={id}>
                {name} ({shops.length} boutique{shops.length > 1 ? "s" : ""})
              </option>
            ))}
          </select>
          <Button
            size="sm"
            disabled={!selectedClientId || isPending}
            onClick={() => {
              attach(selectedClientId);
              setSelectedClientId("");
            }}
          >
            Rattacher
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t pt-4">
          <Input
            value={newClientName}
            maxLength={MAX_LENGTH.name}
            onChange={(event) => setNewClientName(event.target.value)}
            placeholder={`Nom du nouveau client (ex. ${shopName})`}
            className="h-8 max-w-64"
          />
          <Button
            variant="outline"
            size="sm"
            disabled={!newClientName.trim() || isPending}
            onClick={() =>
              createMutation.mutate({
                body: { name: newClientName.trim(), shopIds: [shopId] },
              })
            }
          >
            Créer le client avec cette boutique
          </Button>
        </div>

        {(updateMutation.isError || createMutation.isError) && (
          <p className="text-sm text-destructive">
            L'enregistrement a échoué, rien n'a été modifié.
          </p>
        )}
      </div>
    </ConfigurationSection>
  );
}

/** Internal fields of the shop itself: logistician, go-live day, notes. */
export function ShopAccountSection({
  shopId,
  account,
}: {
  shopId: string;
  account: SuperAdminShopAccountDto;
}) {
  const updateMutation = useUpdateSuperAdminShopAccount();
  const [logistician, setLogistician] = useState(account.logistician ?? "");
  const [goLiveAt, setGoLiveAt] = useState(account.goLiveAt ?? "");
  const [notes, setNotes] = useState(account.notes ?? "");

  const isDirty =
    logistician !== (account.logistician ?? "") ||
    goLiveAt !== (account.goLiveAt ?? "") ||
    notes !== (account.notes ?? "");

  return (
    <ConfigurationSection
      title="Boutique"
      description="Informations internes, jamais visibles du marchand."
    >
      <div className="grid grid-cols-2 gap-4 px-5 py-4">
        <label className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">
            Logisticien
          </span>
          <Input
            value={logistician}
            maxLength={MAX_LENGTH.short}
            onChange={(event) => setLogistician(event.target.value)}
            placeholder="Ex. Crosslog, Internalisé…"
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">
            Mise en prod
          </span>
          <Input
            type="date"
            value={goLiveAt}
            onChange={(event) => setGoLiveAt(event.target.value)}
          />
        </label>
        <label className="col-span-2 space-y-1">
          <span className="text-xs font-medium text-muted-foreground">
            Notes sur la boutique
          </span>
          <Textarea
            value={notes}
            maxLength={MAX_LENGTH.notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={3}
          />
        </label>
        <div className="col-span-2 flex items-center justify-end gap-3">
          {updateMutation.isError && (
            <span className="text-sm text-destructive">
              L'enregistrement a échoué.
            </span>
          )}
          <Button
            size="sm"
            disabled={!isDirty || updateMutation.isPending}
            onClick={() =>
              updateMutation.mutate({
                params: { path: { shopId } },
                body: {
                  logistician: logistician.trim() || null,
                  goLiveAt: goLiveAt || null,
                  notes: notes.trim() || null,
                },
              })
            }
          >
            {updateMutation.isPending ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </div>
    </ConfigurationSection>
  );
}
